import {
  getPostAttachments,
  normalizePostAttachments,
  type PollDefinition,
  type PollState,
  type Post,
} from "@/entities/post/types";
import type {
  ThreadChunkElement,
  ThreadChunkPost,
  ThreadPostState,
} from "@/entities/thread/types";
import { isThreadChunkPost } from "@/entities/thread/types";

export function normalizeThreadPost(
  threadId: string,
  element: ThreadChunkPost,
): Post {
  return normalizePostAttachments({
    ...element,
    threadId,
    status: "public",
    sodaneCount: 0,
    displayIdSource: element.displayIdSource ?? null,
    // sageフィールド追加前のバックエンドからのレスポンスにはsageが
    // 含まれないため、後方互換としてfalseへフォールバックする。
    sage: element.sage ?? false,
    showThreadOwner: element.showThreadOwner === true,
  });
}

export function mergeThreadPosts(
  threadId: string,
  elements: ThreadChunkElement[],
  postStates: ThreadPostState[],
  previousPosts: Post[] = [],
): Post[] {
  const stateBySeq = new Map(postStates.map((state) => [state.seq, state]));
  const previousBySeq = new Map(previousPosts.map((post) => [post.seq, post]));
  const nextPosts = elements.flatMap((element) => {
    if (!isThreadChunkPost(element)) return [];
    const basePost = normalizeThreadPost(threadId, element);
    const state = stateBySeq.get(basePost.seq);
    const nextPost = state ? applyPostState(basePost, state) : basePost;
    if (!isPostVisible(nextPost.status)) return [];
    const previousPost = previousBySeq.get(nextPost.seq);
    return [
      previousPost && arePostsEqual(previousPost, nextPost)
        ? previousPost
        : nextPost,
    ];
  });

  if (
    previousPosts.length === nextPosts.length &&
    nextPosts.every((post, index) => post === previousPosts[index])
  ) {
    return previousPosts;
  }
  return nextPosts;
}

export function mergeFullThreadPostStates(
  posts: Post[],
  postStates: ThreadPostState[] | undefined,
): Post[] {
  if (!postStates) return posts;
  const stateBySeq = new Map(postStates.map((state) => [state.seq, state]));
  const nextPosts = posts.flatMap((post) => {
    const state = stateBySeq.get(post.seq);
    const nextPost = state ? applyPostState(post, state) : post;
    return isPostVisible(nextPost.status) ? [nextPost] : [];
  });
  return nextPosts.length === posts.length &&
    nextPosts.every((post, index) => post === posts[index])
    ? posts
    : nextPosts;
}

export function isPostVisible(status: Post["status"]): boolean {
  return status === "public";
}

function applyPostState(post: Post, state: ThreadPostState): Post {
  const isUnavailable = !isPostVisible(state.status);
  const nextBody = isUnavailable ? "" : post.body;
  const nextAttachments = isUnavailable ? [] : getPostAttachments(post);
  const nextAttachment = nextAttachments[0] ?? null;
  const nextStatus = state.status;
  const nextSodaneCount = state.reactions.up;
  const nextPollState = post.poll ? (state.poll ?? null) : null;

  if (
    post.status === nextStatus &&
    post.body === nextBody &&
    post.attachments === nextAttachments &&
    post.attachment === nextAttachment &&
    post.sodaneCount === nextSodaneCount &&
    arePollStatesEqual(post.pollState ?? null, nextPollState)
  ) {
    return post;
  }

  return {
    ...post,
    status: nextStatus,
    body: nextBody,
    attachments: nextAttachments,
    attachment: nextAttachment,
    sodaneCount: nextSodaneCount,
    pollState: nextPollState,
  };
}

function arePostsEqual(left: Post, right: Post): boolean {
  return (
    left.id === right.id &&
    left.threadId === right.threadId &&
    left.seq === right.seq &&
    left.status === right.status &&
    left.body === right.body &&
    left.createdAt === right.createdAt &&
    areAttachmentListsEqual(
      getPostAttachments(left),
      getPostAttachments(right),
    ) &&
    left.attachment === right.attachment &&
    left.sodaneCount === right.sodaneCount &&
    left.delCount === right.delCount &&
    left.displayId === right.displayId &&
    left.displayIdSource === right.displayIdSource &&
    left.sage === right.sage &&
    left.showThreadOwner === right.showThreadOwner &&
    arePollDefinitionsEqual(left.poll ?? null, right.poll ?? null) &&
    arePollStatesEqual(left.pollState ?? null, right.pollState ?? null)
  );
}

function arePollDefinitionsEqual(
  left: PollDefinition | null,
  right: PollDefinition | null,
): boolean {
  return (
    left === right ||
    (left !== null &&
      right !== null &&
      left.closesAt === right.closesAt &&
      left.options.length === right.options.length &&
      left.options.every(
        (option, index) =>
          option.id === right.options[index]?.id &&
          option.label === right.options[index]?.label,
      ))
  );
}

function arePollStatesEqual(
  left: PollState | null,
  right: PollState | null,
): boolean {
  if (left === right) return true;
  if (left === null || right === null) return false;
  if (
    left.isClosed !== right.isClosed ||
    left.selectedOptionId !== right.selectedOptionId ||
    left.results?.totalVotes !== right.results?.totalVotes
  ) {
    return false;
  }
  const leftOptions = left.results?.options;
  const rightOptions = right.results?.options;
  return (
    leftOptions === rightOptions ||
    (leftOptions !== undefined &&
      rightOptions !== undefined &&
      leftOptions.length === rightOptions.length &&
      leftOptions.every(
        (option, index) =>
          option.optionId === rightOptions[index]?.optionId &&
          option.voteCount === rightOptions[index]?.voteCount,
      ))
  );
}

function areAttachmentListsEqual(
  left: ReturnType<typeof getPostAttachments>,
  right: ReturnType<typeof getPostAttachments>,
): boolean {
  return (
    left === right ||
    (left.length === right.length &&
      left.every((attachment, index) => attachment === right[index]))
  );
}
